import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Avatar,
  Chip,
  Button,
  IconButton,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TablePagination,
  Badge,
  Tooltip,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Delete as DeleteIcon,
  QrCode2 as QrCodeIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  HourglassEmpty as PendingIcon,
  Refresh as RefreshIcon,
  CalendarMonth as MonthIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  CalendarToday as CalendarTodayIcon,
  AccessTime as AccessTimeIcon,
  PhoneAndroid as PhoneAndroidIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import TimeService from '../../../services/TimeService';
import DeviceInfoModal from './DeviceInfoModal';
import { taskDataService, slotsToMonthlyObject } from '../../../services/taskDataService';
import type { TaskDto, SlotDto, CompanyCompletionDto } from '../../../services/taskDataService';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

// Özel stil bileşeni - görev zamanları için
interface TaskTimeChipProps {
  label: string;
  size: 'small' | 'medium';
  status: string;
}

const TaskTimeChip: React.FC<TaskTimeChipProps> = ({ label, size, status }) => {
  let bgColor = '';
  let textColor = '';
  
  switch (status) {
    case 'active':
      bgColor = 'primary.50';
      textColor = 'primary.main';
      break;
    case 'upcoming':
      bgColor = 'info.50';
      textColor = 'info.main';
      break;
    case 'completed':
      bgColor = 'success.50';
      textColor = 'success.main';
      break;
    case 'missed':
      bgColor = 'error.50';
      textColor = 'error.main';
      break;
    case 'started':
      bgColor = 'warning.50';
      textColor = 'warning.main';
      break;
    default:
      bgColor = 'grey.100';
      textColor = 'grey.600';
  }
  
  return (
    <Chip
      label={label}
      size={size}
      sx={{
        height: size === 'small' ? 20 : 24,
        fontSize: size === 'small' ? '0.7rem' : '0.75rem',
        bgcolor: bgColor,
        color: textColor
      }}
    />
  );
};

// Ay ismini döndüren yardımcı fonksiyon
const getMonthName = (monthNumber: number): string => {
  const months = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
  ];
  return months[monthNumber];
};

// Görevi ay/gün şeklinde formatlanmış şekilde döndüren yardımcı fonksiyon
const getTaskMonthDays = (task: any) => {
  if (!task) return [];
  
  const result = [];
  
  // task içindeki ay bilgilerini döngüye alıyoruz
  for (const key in task) {
    if (key.startsWith('month')) {
      const monthNumber = parseInt(key.replace('month', ''));
      const monthData = task[key];
      const monthName = getMonthName(monthNumber);
      
      const days = [];
      
      // Ay içindeki günleri döngüye alıyoruz
      for (const dayKey in monthData) {
        if (dayKey.startsWith('day')) {
          const dayNumber = parseInt(dayKey.replace('day', ''));
          const dayData = monthData[dayKey];
          
          days.push({
            day: dayNumber,
            dailyRepetitions: dayData.dailyRepetitions || 1,
            repetitionTimes: dayData.repetitionTimes || []
          });
        }
      }
      
      if (days.length > 0) {
        // Günleri sayısal olarak sırala
        days.sort((a, b) => a.day - b.day);
        
        result.push({
          month: monthNumber,
          monthName,
          days
        });
      }
    }
  }
  
  // Ayları sayısal olarak sırala
  result.sort((a, b) => a.month - b.month);
  
  return result;
};

interface MonthlyTasksTableProps {
  tasks: any[];
  statusFilter: string;
  onShowTaskDetail: (task: any) => void;
  onOpenQrPrintModal: (task: any, event: React.MouseEvent) => void;
  getStatusColor: (status: string) => string;
  getStatusIcon: (status: string) => React.ReactNode;
  getStatusLabel: (status: string) => string;
  getTaskTimeColor: (task: any, timeString: string) => string;
  companyId: string | null;
  onDeleteTask?: (taskId: string, personnelId: string) => Promise<void>;
  t: TFunction;
  getTaskMonthDays: (task: any) => any[];
}

const MonthlyTasksTable: React.FC<MonthlyTasksTableProps> = ({
  tasks,
  statusFilter,
  onShowTaskDetail,
  onOpenQrPrintModal,
  getStatusColor,
  getStatusIcon,
  getStatusLabel,
  getTaskTimeColor,
  companyId,
  onDeleteTask,
  t,
  getTaskMonthDays
}) => {
  // Sayfalama için state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  
  // Sayfalandırılmış task verileri
  const paginatedTasks = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return tasks.slice(startIndex, startIndex + rowsPerPage);
  }, [tasks, page, rowsPerPage]);
  
  // Filtre değişikliklerinde sayfa numarasını sıfırla
  useEffect(() => {
    setPage(0);
  }, [tasks]);
  
  // Sayfa değişimi
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  // Sayfa başına satır sayısı değişimi
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (tasks.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body1" color="textSecondary">
          {t('tasks.monthlyTasks.table.noTasks')}
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow sx={{ bgcolor: 'background.paper' }}>
            <TableCell width={50}>{t('tasks.monthlyTasks.table.status')}</TableCell>
            <TableCell>{t('tasks.monthlyTasks.table.task')}</TableCell>
            <TableCell>{t('tasks.monthlyTasks.table.months')}</TableCell>
            <TableCell>{t('tasks.monthlyTasks.table.daysAndTimes')}</TableCell>
            <TableCell width={70}>{t('tasks.monthlyTasks.table.tolerance')}</TableCell>
            <TableCell>{t('tasks.monthlyTasks.table.personnel')}</TableCell>
            <TableCell>{t('tasks.monthlyTasks.table.status')}</TableCell>
            {statusFilter !== 'completed' && statusFilter !== 'missed' && (
              <TableCell align="right" width={100}>{t('tasks.monthlyTasks.table.actions')}</TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedTasks.map((task) => (
            <TableRow 
              key={task.id} 
              hover 
              onClick={() => onShowTaskDetail(task)} 
              sx={{
                cursor: statusFilter === 'completed' || statusFilter === 'missed' ? 'default' : 'pointer'
              }}
            >
              <TableCell>
                <Avatar
                  sx={{
                    width: 32, 
                    height: 32, 
                    bgcolor: `${getStatusColor(task.status)}20`,
                    color: getStatusColor(task.status)
                  }}
                >
                  {getStatusIcon(task.status)}
                </Avatar>
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight="medium">
                  {task.name}
                </Typography>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {getTaskMonthDays(task).map((month) => (
                    <Chip
                      key={month.month}
                      label={month.monthName}
                      size="small"
                      icon={<MonthIcon style={{ fontSize: 16 }} />}
                      sx={{ 
                        height: 20, 
                        fontSize: '0.7rem', 
                        bgcolor: 'info.50', 
                        color: 'info.main'
                      }}
                    />
                  ))}
                </Box>
              </TableCell>
              <TableCell>
                {getTaskMonthDays(task).length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {getTaskMonthDays(task).map((month) => (
                      <Box key={month.month} sx={{ display: 'flex', flexDirection: 'column', mb: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                          {month.monthName}:
                        </Typography>
                        {month.days.map((day: any) => (
                          <Box key={day.day} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', minWidth: 24 }}>
                              {day.day}:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {day.repetitionTimes.map((time: string, index: number) => (
                                <TaskTimeChip
                                  key={index}
                                  label={time}
                                  size="small"
                                  status={getTaskTimeColor(task, time)}
                                />
                              ))}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">-</Typography>
                )}
              </TableCell>
              <TableCell>
                <Chip 
                  label={`${task.startTolerance || 15} ${t('tasks.minutes')}`}
                  size="small"
                  sx={{ 
                    height: 20, 
                    fontSize: '0.7rem', 
                    bgcolor: 'primary.50', 
                    color: 'primary.main'
                  }}
                />
              </TableCell>
              <TableCell>
                {task.personnelName ? (
                  <Typography variant="body2">{task.personnelName}</Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">{t('tasks.monthlyTasks.personnelDeleted')}</Typography>
                )}
              </TableCell>
              <TableCell>
                <Chip
                  label={getStatusLabel(task.status)}
                  size="small"
                  sx={{
                    bgcolor: `${getStatusColor(task.status)}20`,
                    color: getStatusColor(task.status),
                    fontWeight: 'medium',
                    height: 20,
                    fontSize: 11,
                  }}
                />
              </TableCell>
              
              {statusFilter !== 'completed' && statusFilter !== 'missed' && (
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                  <IconButton
                    size="small"
                    onClick={(e) => onOpenQrPrintModal(task, e)}
                    sx={{ mr: 1, color: 'primary.main' }}
                  >
                    <QrCodeIcon fontSize="small" />
                  </IconButton>
                  
                  <IconButton
                    size="small"
                    color="info"
                    onClick={(e) => {
                      e.stopPropagation();
                      onShowTaskDetail(task);
                    }}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      
      {/* Sayfalama */}
      {tasks.length > 0 && (
        <TablePagination
          component="div"
          count={tasks.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
          labelRowsPerPage={t('tasks.monthlyTasks.pagination.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => t('tasks.monthlyTasks.pagination.displayedRows', { from, to, count })}
        />
      )}
    </TableContainer>
  );
};

// Tamamlanan aylık görevler için tablo bileşeni
const CompletedMonthlyTasksTable: React.FC<{
  tasks: any[];
  getStatusColor: (status: string) => string;
  getStatusIcon: (status: string) => React.ReactNode;
  getStatusLabel: (status: string) => string;
  tableTitle?: string;
  t: TFunction;
  i18n: any;
}> = ({
  tasks,
  getStatusColor,
  getStatusIcon,
  getStatusLabel,
  tableTitle,
  t,
  i18n
}) => {
  // Sayfalama için state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [selectedDeviceInfo, setSelectedDeviceInfo] = useState<any>(null);
  const [deviceModalOpen, setDeviceModalOpen] = useState(false);

  // Cihaz bilgisi modalını açma işlevi
  const handleOpenDeviceModal = (deviceInfo: any) => {
    setSelectedDeviceInfo(deviceInfo);
    setDeviceModalOpen(true);
  };

  // Cihaz bilgisi modalını kapatma işlevi
  const handleCloseDeviceModal = () => {
    setDeviceModalOpen(false);
  };
  
  // Sayfalandırılmış task verileri
  const paginatedTasks = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return tasks.slice(startIndex, startIndex + rowsPerPage);
  }, [tasks, page, rowsPerPage]);
  
  // Filtre değişikliklerinde sayfa numarasını sıfırla
  useEffect(() => {
    setPage(0);
  }, [tasks]);
  
  // Sayfa değişimi
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  // Sayfa başına satır sayısı değişimi
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Tarihi formatlama fonksiyonu
  const formatTimestamp = (timestamp: number) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString(i18n.language === 'en' ? 'en-US' : 'tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const currentTableTitle = tableTitle || t('tasks.monthlyTasks.completedTable.title');

  return (
    <>
      <TableContainer component={Paper} sx={{ 
        mt: 2, 
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
        borderRadius: 2,
        maxHeight: 'calc(100vh - 240px)',
        overflowY: 'auto'
      }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell width="5%">{t('tasks.monthlyTasks.completedTable.status')}</TableCell>
              <TableCell width="13%">{t('tasks.monthlyTasks.completedTable.taskName')}</TableCell>
              <TableCell width="9%">{t('tasks.monthlyTasks.completedTable.taskDay')}</TableCell>
              <TableCell width="9%">{t('tasks.monthlyTasks.completedTable.taskTime')}</TableCell>
              <TableCell width="9%">{t('tasks.monthlyTasks.table.tolerance')}</TableCell>
              <TableCell width="13%">{t('tasks.monthlyTasks.table.personnel')}</TableCell>
              {currentTableTitle === t('tasks.monthlyTasks.completedTable.title') ? (
                <>
                  <TableCell width="13%">{t('tasks.monthlyTasks.completedTable.startTime')}</TableCell>
                  <TableCell width="13%">{t('tasks.monthlyTasks.completedTable.endTime')}</TableCell>
                  <TableCell width="10%">{t('tasks.monthlyTasks.completedTable.device')}</TableCell>
                </>
              ) : (
                <TableCell width="35%">{t('tasks.monthlyTasks.completedTable.missedTime')}</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedTasks.map((task) => (
              <TableRow 
                key={task.id} 
                hover
                sx={{ cursor: 'default' }}
              >
                <TableCell>
                  <Avatar
                    sx={{
                      width: 32, 
                      height: 32, 
                      bgcolor: `${getStatusColor(task.status)}20`,
                      color: getStatusColor(task.status)
                    }}
                  >
                    {getStatusIcon(task.status)}
                  </Avatar>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {task.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {task.taskDate || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <TaskTimeChip
                    label={task.taskTime || '-'}
                    size="small"
                    status={task.status === 'completed' ? "completed" : "missed"}
                  />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={`${task.startTolerance || 15} ${t('tasks.minutes')}`}
                    size="small"
                    sx={{ 
                      height: 20, 
                      fontSize: '0.7rem', 
                      bgcolor: 'primary.50', 
                      color: 'primary.main'
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color={task.personnelName ? 'text.secondary' : 'error'} sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    fontSize: '0.8rem'
                  }}>
                    <Avatar sx={{ width: 24, height: 24, fontSize: '0.8rem', mr: 1 }}>
                      {task.personnelName ? task.personnelName.charAt(0) : '-'}
                    </Avatar>
                    {task.personnelName || t('tasks.monthlyTasks.unassigned')}
                  </Typography>
                </TableCell>
                {currentTableTitle === t('tasks.monthlyTasks.completedTable.title') ? (
                  <>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                        {formatTimestamp(task.startedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="success.main" fontSize="0.8rem" fontWeight="medium">
                        {formatTimestamp(task.completedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {task.deviceInfo ? (
                        <Tooltip title={t('tasks.monthlyTasks.completedTable.deviceInfoTooltip')}>
                          <Button
                            size="small"
                            startIcon={<PhoneAndroidIcon />}
                            onClick={() => handleOpenDeviceModal(task.deviceInfo)}
                            sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                          >
                            {task.deviceInfo.model || task.deviceInfo.deviceType}
                          </Button>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                          -
                        </Typography>
                      )}
                    </TableCell>
                  </>
                ) : (
                  <TableCell>
                    <Typography variant="body2" color="error.main" fontSize="0.8rem" fontWeight="medium">
                      {formatTimestamp(task.missedAt)}
                    </Typography>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {tasks.length === 0 && (
              <TableRow>
                <TableCell colSpan={currentTableTitle === t('tasks.monthlyTasks.completedTable.title') ? 9 : 7} align="center" sx={{ py: 3 }}>
                  <Typography color="text.secondary">
                    {currentTableTitle === t('tasks.monthlyTasks.completedTable.title')
                      ? t('tasks.monthlyTasks.completedTable.noCompletedTasks') 
                      : t('tasks.monthlyTasks.completedTable.noMissedTasks')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        
        {/* Sayfalama */}
        {tasks.length > 0 && (
          <TablePagination
            component="div"
            count={tasks.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[25, 50, 100]}
            labelRowsPerPage={t('tasks.monthlyTasks.pagination.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) => t('tasks.monthlyTasks.pagination.displayedRows', { from, to, count })}
          />
        )}
      </TableContainer>

      {/* Cihaz Bilgileri Modalı */}
      <DeviceInfoModal
        open={deviceModalOpen}
        onClose={handleCloseDeviceModal}
        deviceInfo={selectedDeviceInfo}
      />
    </>
  );
};

// Ana MonthlyTasks bileşeni
interface MonthlyTasksProps {
  companyId: string | null;
  statusFilter: string;
  personnelFilter: string;
  taskSearchTerm: string;
  viewMode: 'card' | 'list';
  personnel: any[];
  onShowTaskDetail: (task: any) => void;
  onOpenQrPrintModal: (task: any, event: React.MouseEvent) => void;
  getStatusColor: (status: string) => string;
  getStatusIcon: (status: string) => React.ReactNode;
  getStatusLabel: (status: string) => string;
  getTaskTimeColor: (task: any, timeString: string) => string;
  onDeleteTask?: (taskId: string, personnelId: string) => Promise<void>;
  onMonthlyTasksDataChange?: (tasks: any[]) => void;
  branchId?: string | null;
  isManager?: boolean;
  reloadKey?: number;
}

const MonthlyTasks: React.FC<MonthlyTasksProps> = ({
  companyId,
  statusFilter,
  personnelFilter,
  taskSearchTerm,
  viewMode,
  personnel,
  onShowTaskDetail,
  onOpenQrPrintModal,
  getStatusColor,
  getStatusIcon,
  getStatusLabel,
  getTaskTimeColor,
  onDeleteTask,
  onMonthlyTasksDataChange,
  branchId,
  isManager = false,
  reloadKey = 0
}) => {
  const { t, i18n } = useTranslation('common2');
  const [monthlyTasks, setMonthlyTasks] = useState<any[]>([]);
  const [completedMonthlyTasks, setCompletedMonthlyTasks] = useState<any[]>([]);
  const [missedMonthlyTasks, setMissedMonthlyTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const getMonthName = useCallback((monthNumber: number): string => {
    const monthKey = [
      'january', 'february', 'march', 'april', 'may', 'june', 
      'july', 'august', 'september', 'october', 'november', 'december'
    ][monthNumber];
    return t(`tasks.monthlyTasks.months.${monthKey}`);
  }, [t]);

  const getTaskMonthDays = useCallback((task: any) => {
    if (!task) return [];
  
    const result = [];
    
    // task içindeki ay bilgilerini döngüye alıyoruz
    for (const key in task) {
      if (key.startsWith('month')) {
        const monthNumber = parseInt(key.replace('month', ''));
        const monthData = task[key];
        const monthName = getMonthName(monthNumber);
        
        const days = [];
        
        // Ay içindeki günleri döngüye alıyoruz
        for (const dayKey in monthData) {
          if (dayKey.startsWith('day')) {
            const dayNumber = parseInt(dayKey.replace('day', ''));
            const dayData = monthData[dayKey];
            
            days.push({
              day: dayNumber,
              dailyRepetitions: dayData.dailyRepetitions || 1,
              repetitionTimes: dayData.repetitionTimes || []
            });
          }
        }
        
        if (days.length > 0) {
          // Günleri sayısal olarak sırala
          days.sort((a, b) => a.day - b.day);
          
          result.push({
            month: monthNumber,
            monthName,
            days
          });
        }
      }
    }
    
    // Ayları sayısal olarak sırala
    result.sort((a, b) => a.month - b.month);
    
    return result;
  }, [getMonthName]);
  
  // Aylık görevleri SQL'den çek (Faz 5 DILIM 4) — görev + slot + tamamlama.
  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const run = async () => {
      try {
        const personnelMap: Record<string, any> = {};
        (personnel || []).forEach((p: any) => { if (p?.id) personnelMap[p.id] = p; });
        const nameFromCompletion = (c: CompanyCompletionDto): string => {
          if (c.personnelFirstName) return `${c.personnelFirstName} ${c.personnelLastName || ''}`.trim();
          return personnelMap[c.personnelFirebaseUid || '']?.name || t('tasks.monthlyTasks.unassigned');
        };
        const toEpoch = (iso?: string | null): number | undefined =>
          iso ? new Date(iso).getTime() : undefined;
        const deviceFromCompletion = (c: CompanyCompletionDto): any =>
          (c.devicePlatform || c.deviceUserAgent)
            ? { model: c.devicePlatform, deviceType: c.devicePlatform, platform: c.devicePlatform, userAgent: c.deviceUserAgent }
            : null;

        const fetchTasks = async (): Promise<TaskDto[]> => {
          const acc: TaskDto[] = [];
          let page = 1;
          for (;;) {
            const res = await taskDataService.listTasks({ taskType: 'monthly', page, pageSize: 200 });
            acc.push(...res.items);
            if (res.items.length === 0 || page >= (res.totalPages || 1)) break;
            page++;
          }
          return acc;
        };
        const fetchCompletions = async (status: 'completed' | 'missed'): Promise<CompanyCompletionDto[]> => {
          const acc: CompanyCompletionDto[] = [];
          let page = 1;
          for (;;) {
            const res = await taskDataService.listCompletions({ taskType: 'monthly', status, page, pageSize: 1000 });
            acc.push(...res.items);
            if (res.items.length === 0 || page >= (res.totalPages || 1)) break;
            page++;
          }
          return acc;
        };

        const [tasksData, slotsData, completedData, missedData] = await Promise.all([
          fetchTasks(),
          taskDataService.listSlots('monthly'),
          fetchCompletions('completed'),
          fetchCompletions('missed'),
        ]);
        if (cancelled) return;

        // Slotları görev bazında grupla → RTDB-uyumlu `month{N}.day{D}` anahtarları
        const slotsByTask: Record<number, SlotDto[]> = {};
        (slotsData || []).forEach((s) => {
          (slotsByTask[s.taskId] = slotsByTask[s.taskId] || []).push(s);
        });

        const tasksList = (tasksData || []).map((tk) => ({
          id: tk.id,
          name: tk.name || t('tasks.monthlyTasks.unnamedTask'),
          description: tk.description || '',
          status: tk.status || 'pending',
          createdAt: tk.createdAt ? new Date(tk.createdAt).getTime() : Date.now(),
          personnelId: tk.personnelFirebaseUid || '',
          personnelName: personnelMap[tk.personnelFirebaseUid || '']?.name || t('tasks.monthlyTasks.personnelNotFound'),
          completionType: tk.completionType || 'button',
          startTolerance: tk.startTolerance || 15,
          groupId: tk.groupId ?? undefined,
          // getTaskMonthDays bu `month{N}.day{D}` anahtarlarını okur:
          ...slotsToMonthlyObject(slotsByTask[tk.id] || []),
        }));
        setMonthlyTasks(tasksList.sort((a, b) => b.createdAt - a.createdAt));

        const completedTasksList = (completedData || []).map((c) => ({
          id: `${c.taskId}_${c.taskDate}_${c.taskTime}`,
          taskId: c.taskId,
          name: c.taskName || t('tasks.monthlyTasks.unnamedTask'),
          description: c.taskDescription || '',
          status: 'completed',
          taskDate: c.taskDate,
          taskTime: c.taskTime,
          startedAt: toEpoch(c.startedAt),
          completedAt: toEpoch(c.completedAt),
          personnelId: c.personnelFirebaseUid || '',
          personnelName: nameFromCompletion(c),
          startTolerance: c.startTolerance || 15,
          deviceInfo: deviceFromCompletion(c),
          fromDatabase: true,
        }));
        setCompletedMonthlyTasks(completedTasksList.sort((a, b) => (b.completedAt || 0) - (a.completedAt || 0)));

        const missedTasksList = (missedData || []).map((c) => ({
          id: `${c.taskId}_${c.taskDate}_${c.taskTime}`,
          taskId: c.taskId,
          name: c.taskName || t('tasks.monthlyTasks.unnamedTask'),
          description: c.taskDescription || '',
          status: 'missed',
          taskDate: c.taskDate,
          taskTime: c.taskTime,
          missedAt: toEpoch(c.missedAt),
          personnelId: c.personnelFirebaseUid || '',
          personnelName: nameFromCompletion(c),
          startTolerance: c.startTolerance || 15,
          deviceInfo: deviceFromCompletion(c),
          fromDatabase: true,
        }));
        setMissedMonthlyTasks(missedTasksList.sort((a, b) => (b.missedAt || 0) - (a.missedAt || 0)));
      } catch (error) {
        if (!cancelled) console.error('Aylık görevler yüklenirken hata:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [companyId, personnel, t, reloadKey]);
  
  // Görevleri filtrele - durum, personel ve arama terimine göre
  const filteredTasks = React.useMemo(() => {
    let result = [...monthlyTasks];
    
    // Şube yöneticisi ve şube ID'si varsa, şubeye göre filtrele
    if (isManager && branchId) {
      result = result.filter(task => {
        // Görevin personeline göre şube kontrolü
        if (task.personnelId) {
          const person = personnel.find(p => p.id === task.personnelId);
          if (person) {
            // branchesId bir nesne olabilir veya string olabilir
            if (typeof person.branchesId === 'object' && person.branchesId !== null) {
              // Nesne durumunda, anahtarlarını kontrol et
              return Object.keys(person.branchesId).includes(branchId);
            } else {
              // String durumunda direk karşılaştır
              return person.branchesId === branchId;
            }
          }
        }
        return false;
      });
    }
    
    // Duruma göre filtrele
    if (statusFilter !== 'all') {
      if (statusFilter === 'completed') {
        // Tamamlanan görevler CompletedMonthlyTasksTable'da gösterildiğinden boş dizi döndürüyoruz
        return [];
      }
      
      if (statusFilter === 'missed') {
        // Kaçırılan görevler için
        return [];
      }
      
      result = result.filter(task => task.status === statusFilter);
    }
    
    // Personele göre filtrele
    if (personnelFilter !== 'all') {
      result = result.filter(task => task.personnelId === personnelFilter);
    }
    
    // Arama terimine göre filtrele
    if (taskSearchTerm) {
      const searchLower = taskSearchTerm.toLowerCase();
      result = result.filter(task => 
        task.name.toLowerCase().includes(searchLower) || 
        task.description.toLowerCase().includes(searchLower) ||
        task.personnelName.toLowerCase().includes(searchLower)
      );
    }
    
    return result;
  }, [monthlyTasks, statusFilter, personnelFilter, taskSearchTerm, isManager, branchId, personnel]);
  
  // Tamamlanan görevleri filtrele
  const filteredCompletedTasks = useMemo(() => {
    let result = [...completedMonthlyTasks];
    
    // Şube yöneticisi ve şube ID'si varsa, şubeye göre filtrele
    if (isManager && branchId) {
      result = result.filter(task => {
        // Görevin personeline göre şube kontrolü
        if (task.personnelId) {
          const person = personnel.find(p => p.id === task.personnelId);
          if (person) {
            // branchesId bir nesne olabilir veya string olabilir
            if (typeof person.branchesId === 'object' && person.branchesId !== null) {
              // Nesne durumunda, anahtarlarını kontrol et
              return Object.keys(person.branchesId).includes(branchId);
            } else {
              // String durumunda direk karşılaştır
              return person.branchesId === branchId;
            }
          }
        }
        return false;
      });
    }
    
    // Personel filtresi uygulanıyor
    if (personnelFilter !== 'all') {
      result = result.filter(task => task.personnelId === personnelFilter);
    }

    // Görev arama filtresi uygulanıyor
    if (taskSearchTerm !== '') {
      result = result.filter(task => 
        task.name.toLowerCase().includes(taskSearchTerm.toLowerCase()) ||
        task.description.toLowerCase().includes(taskSearchTerm.toLowerCase())
      );
    }
    
    return result;
  }, [completedMonthlyTasks, personnelFilter, taskSearchTerm, isManager, branchId, personnel]);
  
  // Filtrelenmiş kaçırılan görevler
  const filteredMissedTasks = useMemo(() => {
    let result = [...missedMonthlyTasks];
    
    // Şube yöneticisi ve şube ID'si varsa, şubeye göre filtrele
    if (isManager && branchId) {
      result = result.filter(task => {
        // Görevin personeline göre şube kontrolü
        if (task.personnelId) {
          const person = personnel.find(p => p.id === task.personnelId);
          if (person) {
            // branchesId bir nesne olabilir veya string olabilir
            if (typeof person.branchesId === 'object' && person.branchesId !== null) {
              // Nesne durumunda, anahtarlarını kontrol et
              return Object.keys(person.branchesId).includes(branchId);
            } else {
              // String durumunda direk karşılaştır
              return person.branchesId === branchId;
            }
          }
        }
        return false;
      });
    }
    
    // Personel filtresi uygulanıyor
    if (personnelFilter !== 'all') {
      result = result.filter(task => task.personnelId === personnelFilter);
    }

    // Görev arama filtresi uygulanıyor
    if (taskSearchTerm !== '') {
      result = result.filter(task => 
        task.name.toLowerCase().includes(taskSearchTerm.toLowerCase()) ||
        task.description.toLowerCase().includes(taskSearchTerm.toLowerCase())
      );
    }
    
    return result;
  }, [missedMonthlyTasks, personnelFilter, taskSearchTerm, isManager, branchId, personnel]);
  
  // Filtrelenmiş verileri üst bileşene gönder
  useEffect(() => {
    if (onMonthlyTasksDataChange) {
      const tasksToExport = statusFilter === 'completed' ? filteredCompletedTasks : 
                            statusFilter === 'missed' ? filteredMissedTasks : 
                            filteredTasks;
      onMonthlyTasksDataChange(tasksToExport);
    }
  }, [filteredTasks, filteredCompletedTasks, filteredMissedTasks, statusFilter, onMonthlyTasksDataChange]);
  
  // Yükleniyor
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', my: 4 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }
  
  // Görev yoksa
  if (monthlyTasks.length === 0 && statusFilter !== 'completed' && statusFilter !== 'missed') {
    return (
      <Box sx={{ textAlign: 'center', my: 8 }}>
        <AssignmentIcon sx={{ fontSize: 60, color: 'primary.light', mb: 2 }} />
        <Typography variant="h6" color="textSecondary">
          {
            statusFilter === 'all' && personnelFilter === 'all'
              ? t('tasks.monthlyTasks.noTasks.noneAdded')
              : t('tasks.monthlyTasks.noTasks.noMatchFilters')
          }
        </Typography>
      </Box>
    );
  }

  if (statusFilter === 'completed' && filteredCompletedTasks.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', my: 8 }}>
        <AssignmentIcon sx={{ fontSize: 60, color: 'primary.light', mb: 2 }} />
        <Typography variant="h6" color="textSecondary">
          {t('tasks.monthlyTasks.noTasks.noCompleted')}
        </Typography>
      </Box>
    );
  }

  if (statusFilter === 'missed' && filteredMissedTasks.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', my: 8 }}>
        <AssignmentIcon sx={{ fontSize: 60, color: 'primary.light', mb: 2 }} />
        <Typography variant="h6" color="textSecondary">
          {t('tasks.monthlyTasks.noTasks.noMissed')}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {viewMode === 'card' ? (
        <Grid container spacing={3}>
          {statusFilter === 'completed' && filteredCompletedTasks.length > 0 ? (
            // Tamamlanan görevler için kart görünümü
            filteredCompletedTasks.map((task) => (
              <Grid item xs={12} sm={6} md={4} key={task.id}>
                <Card 
                  onClick={() => onShowTaskDetail(task)}
                  sx={{
                    height: '100%',
                    borderRadius: 12,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: '0 6px 16px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <CardContent>
                    {/* Kart başlığı */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: `${getStatusColor('completed')}20`,
                          color: getStatusColor('completed'),
                          mr: 1,
                        }}
                      >
                        {getStatusIcon('completed')}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" noWrap>
                          {task.name}
                        </Typography>
                        <Chip
                          label={getStatusLabel('completed')}
                          size="small"
                          sx={{
                            bgcolor: `${getStatusColor('completed')}20`,
                            color: getStatusColor('completed'),
                            fontWeight: 'medium',
                            height: 20,
                            fontSize: 11,
                          }}
                        />
                      </Box>
                    </Box>

                    <Divider sx={{ mb: 1.5 }} />

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 1.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        flexGrow: 0,
                      }}
                    >
                      {task.description || t('tasks.monthlyTasks.noDescription')}
                    </Typography>

                    {/* Aylar */}
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="body2" fontWeight="medium" color="primary" gutterBottom>
                        {t('tasks.monthlyTasks.card.months')}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {getTaskMonthDays(task).map((month) => (
                          <Chip
                            key={month.month}
                            label={month.monthName}
                            size="small"
                            icon={<MonthIcon style={{ fontSize: 16 }} />}
                            sx={{ 
                              height: 20, 
                              fontSize: '0.7rem', 
                              bgcolor: 'info.50', 
                              color: 'info.main'
                            }}
                          />
                        ))}
                      </Box>
                    </Box>

                    {/* Görev Saatleri */}
                    {getTaskMonthDays(task).length > 0 && (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="body2" fontWeight="medium" color="primary" gutterBottom>
                          {t('tasks.monthlyTasks.card.taskHours')}
                        </Typography>
                        {getTaskMonthDays(task).map((month) => (
                          <Box key={month.month} sx={{ mb: 1 }}>
                            <Typography variant="caption" fontWeight="bold">
                              {month.monthName}:
                            </Typography>
                            {month.days.map((dayData) => (
                              <Box key={dayData.day} sx={{ mt: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 'bold', minWidth: 24, display: 'inline-block' }}>
                                  {dayData.day}.
                                </Typography>
                                <Box sx={{ display: 'inline-flex', flexWrap: 'wrap', gap: 0.5, ml: 1 }}>
                                  {dayData.repetitionTimes.map((time: string, index: number) => (
                                    <TaskTimeChip
                                      key={index}
                                      label={time}
                                      size="small"
                                      status={getTaskTimeColor(task, time)}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Tamamlanma zamanı */}
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="body2" fontWeight="medium" color="success.main" gutterBottom>
                        {t('tasks.monthlyTasks.card.completionTime')}
                      </Typography>
                      <Typography variant="body2">
                        {task.completedAtFormatted}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">
                        <b>{t('tasks.monthlyTasks.card.personnel')}</b> {task.personnelName}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : statusFilter === 'missed' && filteredMissedTasks.length > 0 ? (
            // Kaçırılan görevler için kart görünümü
            filteredMissedTasks.map((task) => (
              <Grid item xs={12} sm={6} md={4} key={task.id}>
                <Card 
                  onClick={() => onShowTaskDetail(task)}
                  sx={{
                    height: '100%',
                    borderRadius: 12,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: '0 6px 16px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <CardContent>
                    {/* Kart başlığı */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: `${getStatusColor('missed')}20`,
                          color: getStatusColor('missed'),
                          mr: 1,
                        }}
                      >
                        {getStatusIcon('missed')}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" noWrap>
                          {task.name}
                        </Typography>
                        <Chip
                          label={getStatusLabel('missed')}
                          size="small"
                          sx={{
                            bgcolor: `${getStatusColor('missed')}20`,
                            color: getStatusColor('missed'),
                            fontWeight: 'medium',
                            height: 20,
                            fontSize: 11,
                          }}
                        />
                      </Box>
                    </Box>

                    <Divider sx={{ mb: 1.5 }} />

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 1.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        flexGrow: 0,
                      }}
                    >
                      {task.description || t('tasks.monthlyTasks.noDescription')}
                    </Typography>

                    {/* Aylar */}
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="body2" fontWeight="medium" color="primary" gutterBottom>
                        {t('tasks.monthlyTasks.card.months')}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {getTaskMonthDays(task).map((month) => (
                          <Chip
                            key={month.month}
                            label={month.monthName}
                            size="small"
                            icon={<MonthIcon style={{ fontSize: 16 }} />}
                            sx={{ 
                              height: 20, 
                              fontSize: '0.7rem', 
                              bgcolor: 'info.50', 
                              color: 'info.main'
                            }}
                          />
                        ))}
                      </Box>
                    </Box>

                    {/* Görev Saatleri */}
                    {getTaskMonthDays(task).length > 0 && (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="body2" fontWeight="medium" color="primary" gutterBottom>
                          {t('tasks.monthlyTasks.card.taskHours')}
                        </Typography>
                        {getTaskMonthDays(task).map((month) => (
                          <Box key={month.month} sx={{ mb: 1 }}>
                            <Typography variant="caption" fontWeight="bold">
                              {month.monthName}:
                            </Typography>
                            {month.days.map((dayData) => (
                              <Box key={dayData.day} sx={{ mt: 0.5 }}>
                                <Typography variant="caption" sx={{ display: 'inline-block', minWidth: 24 }}>
                                  {dayData.day}.
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                  {dayData.repetitionTimes.map((time: string, index: number) => (
                                    <TaskTimeChip
                                      key={index}
                                      label={time}
                                      size="small"
                                      status={getTaskTimeColor(task, time)}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Kaçırılma zamanı */}
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="body2" fontWeight="medium" color="error.main" gutterBottom>
                        {t('tasks.monthlyTasks.card.missedTime')}
                      </Typography>
                      <Typography variant="body2">
                        {task.missedAtFormatted}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">
                        <b>{t('tasks.monthlyTasks.card.personnel')}</b> {task.personnelName}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            // Normal görevler için kart görünümü
            filteredTasks.map((task) => (
              <Grid item xs={12} sm={6} md={4} key={task.id}>
                <Card 
                  onClick={() => onShowTaskDetail(task)}
                  sx={{
                    height: '100%',
                    borderRadius: 12,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                    cursor: statusFilter === 'completed' || statusFilter === 'missed' ? 'default' : 'pointer',
                    '&:hover': {
                      boxShadow: statusFilter === 'completed' || statusFilter === 'missed' 
                        ? '0 4px 12px rgba(0,0,0,0.05)' 
                        : '0 6px 16px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: `${getStatusColor(task.status)}20`,
                          color: getStatusColor(task.status),
                          mr: 1,
                        }}
                      >
                        {getStatusIcon(task.status)}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" noWrap>
                          {task.name}
                        </Typography>
                        <Chip
                          label={getStatusLabel(task.status)}
                          size="small"
                          sx={{
                            bgcolor: `${getStatusColor(task.status)}20`,
                            color: getStatusColor(task.status),
                            fontWeight: 'medium',
                            height: 20,
                            fontSize: 11,
                          }}
                        />
                      </Box>
                    </Box>

                    <Divider sx={{ mb: 1.5 }} />

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 1.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        flexGrow: 0,
                      }}
                    >
                      {task.description || t('tasks.monthlyTasks.noDescription')}
                    </Typography>

                    {/* Ay çipleri */}
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="body2" fontWeight="medium" color="primary" gutterBottom>
                        {t('tasks.monthlyTasks.card.months')}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {getTaskMonthDays(task).map((month) => (
                          <Chip
                            key={month.month}
                            label={month.monthName}
                            size="small"
                            icon={<MonthIcon style={{ fontSize: 16 }} />}
                            sx={{ 
                              height: 20, 
                              fontSize: '0.7rem', 
                              bgcolor: 'info.50', 
                              color: 'info.main'
                            }}
                          />
                        ))}
                      </Box>
                    </Box>

                    {/* Ay/Gün/Saat bilgileri */}
                    {getTaskMonthDays(task).length > 0 && (
                      <Box sx={{ mb: 1.5 }}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body2" fontWeight="medium" color="primary" gutterBottom>
                          {t('tasks.monthlyTasks.card.taskHours')}
                        </Typography>
                        {getTaskMonthDays(task).map((month) => (
                          <Box key={month.month} sx={{ mb: 1.5 }}>
                            <Typography variant="caption" fontWeight="bold">
                              {month.monthName}:
                            </Typography>
                            {month.days.map((dayData) => (
                              <Box key={dayData.day} sx={{ mt: 0.5 }}>
                                <Typography variant="caption" sx={{ display: 'inline-block', minWidth: 24 }}>
                                  {dayData.day}.
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                  {dayData.repetitionTimes.map((time: string, index: number) => (
                                    <TaskTimeChip
                                      key={index}
                                      label={time}
                                      size="small"
                                      status={getTaskTimeColor(task, time)}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        ))}
                        <Chip 
                          label={t('tasks.monthlyTasks.card.toleranceSuffix', { minutes: task.startTolerance || 15 })}
                          size="small"
                          sx={{ 
                            mt: 1,
                            height: 20, 
                            fontSize: '0.7rem', 
                            bgcolor: 'primary.50', 
                            color: 'primary.main'
                          }}
                        />
                      </Box>
                    )}

                    <Divider sx={{ my: 1 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">
                        <b>{t('tasks.monthlyTasks.card.personnel')}</b> {task.personnelName}
                      </Typography>
                      
                      <Box>
                        {statusFilter !== 'completed' && statusFilter !== 'missed' && (
                          <>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onOpenQrPrintModal(task, e);
                              }}
                              sx={{ mr: 1, color: 'primary.main' }}
                            >
                              <QrCodeIcon fontSize="small" />
                            </IconButton>
                            
                            <IconButton
                              size="small"
                              color="info"
                              onClick={(e) => {
                                e.stopPropagation();
                                onShowTaskDetail(task);
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      ) : (
        <>
          {statusFilter === 'completed' ? (
            // Tamamlanan görevler için özel tablo gösterimi
            <CompletedMonthlyTasksTable 
              tasks={filteredCompletedTasks}
              getStatusColor={getStatusColor}
              getStatusIcon={getStatusIcon}
              getStatusLabel={getStatusLabel}
              tableTitle={t('tasks.monthlyTasks.completedTable.title')}
              t={t}
              i18n={i18n}
            />
          ) : statusFilter === 'missed' ? (
            // Kaçırılan görevler için özel tablo gösterimi
            <CompletedMonthlyTasksTable 
              tasks={filteredMissedTasks}
              getStatusColor={getStatusColor}
              getStatusIcon={getStatusIcon}
              getStatusLabel={getStatusLabel}
              tableTitle={t('tasks.monthlyTasks.completedTable.missedTitle')}
              t={t}
              i18n={i18n}
            />
          ) : (
            // Normal görevler için standart tablo gösterimi
            <MonthlyTasksTable 
              tasks={filteredTasks}
              statusFilter={statusFilter}
              onShowTaskDetail={onShowTaskDetail}
              onOpenQrPrintModal={onOpenQrPrintModal}
              getStatusColor={getStatusColor}
              getStatusIcon={getStatusIcon}
              getStatusLabel={getStatusLabel}
              getTaskTimeColor={getTaskTimeColor}
              companyId={companyId}
              onDeleteTask={onDeleteTask}
              t={t}
              getTaskMonthDays={getTaskMonthDays}
            />
          )}
        </>
      )}
    </>
  );
};

export default MonthlyTasks; 